import React, {useEffect, useState} from 'react';
import {Box, Button, CircularProgress, Typography} from '@mui/material';
import {useDispatch} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import api from '../../../api/axios';
import {setSignUpStep} from '../../../store/slices/authSlice';

const TIMER_SECONDS = 8;
const CHROME_URL = 'https://chrome.google.com/webstore/detail/MoonSkins.GG';
const FIREFOX_URL = 'https://addons.mozilla.org/en-US/firefox/addon/MoonSkinsGG';

const ChromeLogo = () => (
    <img
        src="https://upload.wikimedia.org/wikipedia/commons/e/e1/Google_Chrome_icon_%28February_2022%29.svg"
        alt="Chrome"
        className="block w-12 h-12"
    />
);

const FirefoxLogo = () => (
    <img
        src="https://upload.wikimedia.org/wikipedia/commons/a/a0/Firefox_logo%2C_2019.svg"
        alt="Firefox"
        className="block w-12 h-12"
    />
);

const SignupDownloadExtensionStep: React.FC = () => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const [secondsLeft, setSecondsLeft] = useState(TIMER_SECONDS);
  const [loading, setLoading] = useState(false);

  const userAgent = navigator.userAgent.toLowerCase();
  const isFirefox = userAgent.includes('firefox');
  const isChrome = userAgent.includes('chrome') && !userAgent.includes('edg') && !userAgent.includes('opr');

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const timer = setInterval(() => setSecondsLeft((prev) => prev - 1), 1000);
    return () => clearInterval(timer);
  }, [secondsLeft]);

  const handleFinish = async () => {
    if (secondsLeft > 0 || loading) return;
    try {
      setLoading(true);
      await api.post('/user/confirm-extension-step');
      dispatch(setSignUpStep('COMPLETED'));
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
      <div className="mt-6 flex flex-col items-center text-center">
        <Typography variant="h6" className="font-bold mb-4 uppercase tracking-wide text-gray-100">
          Nutzen Sie jetzt unsere MoonSkin.GG Extension
        </Typography>

        <Typography variant="body1" className="text-gray-300 max-w-md mb-8">
          Um noch schneller und sicherer zu handeln, empfehlen wir die Installation unserer Browser-Erweiterung.
        </Typography>

        <div className="flex gap-6 mt-6 mb-10 justify-center">
          {(isChrome || (!isChrome && !isFirefox)) && (
              <Button
                  variant="contained"
                  component="a"
                  href={CHROME_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-3 p-8 border border-gray-700 rounded-xl transition-all w-44 h-28 normal-case border-solid"
                  sx={{
                    flexDirection: 'column',
                    backgroundColor: 'rgba(255, 255, 255, 0.03)',
                    '&:hover': {backgroundColor: 'rgba(255, 255, 255, 0.08)'}
                  }}
              >
                <ChromeLogo/>
                <Typography variant="body2" className="font-semibold text-white mt-1">
                  Chrome
                </Typography>
              </Button>
          )}

          {(isChrome || (!isChrome && !isFirefox)) && (
              <Button
                  variant="contained"
                  component="a"
                  href={FIREFOX_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-3 p-8 border border-gray-700 rounded-xl transition-all w-44 h-28 normal-case border-solid"
                  sx={{
                    flexDirection: 'column',
                    backgroundColor: 'rgba(255, 255, 255, 0.03)',
                    '&:hover': {backgroundColor: 'rgba(255, 255, 255, 0.08)'}
                  }}
              >
                <FirefoxLogo/>
                <Typography variant="body2" className="font-semibold text-white mt-1">
                  Firefox
                </Typography>
              </Button>
          )}
        </div>

        <Box className="w-full flex justify-end mt-4">
          <Button
              variant="contained"
              onClick={handleFinish}
              disabled={secondsLeft > 0 || loading}
              className="w-[220px] h-[48px] relative"
              sx={{textTransform: 'none'}}
          >
          <span className={loading ? 'invisible' : ''}>
            {secondsLeft > 0
                ? `${t('common.continue')} (${secondsLeft}s)`
                : t('common.continue')}
          </span>
            {loading && (
                <CircularProgress
                    size={24}
                    color="inherit"
                    className="absolute left-1/2 top-1/2 -ml-[12px] -mt-[12px]"
                />
            )}
          </Button>
        </Box>
      </div>
  );
};

export default SignupDownloadExtensionStep;